// premium-about: mission, team signals, and social proof with KPIs
import React from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '@/components/navigation';
import { Footer } from '@/components/footer';
import { Button } from '@/components/ui/button';
import { ArrowRight, Target, Users, TrendingUp, Clock } from 'lucide-react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';
import { SEO } from '@/components/seo';

const principles = [
  {
    icon: Target,
    title: "Clear plan, no surprises",
    description: "Every project starts with defined scope, timeline, and success metrics."
  },
  {
    icon: Users,
    title: "Senior team on your project",
    description: "Work directly with experienced developers and designers, not junior staff."
  },
  {
    icon: TrendingUp,
    title: "Measurable results",
    description: "We track real outcomes: conversions, user engagement, and business impact."
  },
  {
    icon: Clock,
    title: "Weekly updates you can trust",
    description: "Consistent communication with demos, progress reports, and next steps."
  }
];

// What clients value about working with us
const clientValues = [
  "We speak human (no jargon)",
  "Predictable delivery (weekly demos)",
  "Post-launch partnership (we stay to help)",
  "Business outcomes first (not just pretty UIs)"
];

// Example outcomes (clearly labeled as examples)
const exampleOutcomes = [
  {
    type: "Service business",
    outcome: "clearer copy + booking flow → more inquiries",
    label: "Example outcome"
  },
  {
    type: "E-commerce",
    outcome: "faster product pages + simplified checkout → fewer drop-offs",
    label: "Example outcome"
  },
  {
    type: "B2B",
    outcome: "focused landing pages → more qualified leads",
    label: "Example outcome"
  }
];

const About = () => {
  const { elementRef, getRevealStyle } = useScrollReveal();

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About AffinityAim",
    "description": "Learn about AffinityAim, a web development company dedicated to engineering human connection through code.",
    "url": "https://www.affinityaim.com/about"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.affinityaim.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "About",
        "item": "https://www.affinityaim.com/about"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="About AffinityAim - Web Development Company Since 2020 | Our Story"
        description="Learn about AffinityAim, a web development company dedicated to engineering human connection through code. Meet our team and principles."
        canonical="https://www.affinityaim.com/about"
        keywords="about AffinityAim, web development company, our team, our mission"
        schema={[aboutSchema, breadcrumbSchema]}
      />
      <Navigation />

      <main id="main-content">
        {/* Hero */}
        <section className="section-spacing bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-content mx-auto text-center">
              
              <div className="mb-16">
                <div className="micro-label mb-6">ABOUT US</div>
                <h1 className="font-display font-bold text-foreground mb-6" style={{ fontSize: '56px', lineHeight: '1.1', letterSpacing: '-0.01em' }}>
                  We build digital products
                  <br />
                  <span className="bg-gradient-primary bg-clip-text text-transparent">
                    that actually work
                  </span>
                </h1>
                <p className="text-body-lg text-muted-foreground max-w-paragraph mx-auto">
                  AffinityAim is a premium app & web app studio founded in 2023. We help businesses grow through custom applications and conversion-focused websites.
                </p>
              </div>

            </div>
          </div>
        </section>

      {/* Mission & Principles */}
      <section className="py-20 bg-background-secondary">
        <div className="container mx-auto px-6">
          <div className="max-w-content mx-auto">
            
            <div className="text-center mb-16">
              <h2 className="font-display font-semibold text-foreground mb-6" style={{ fontSize: '32px', lineHeight: '1.2' }}>
                Our principles
              </h2>
              <p className="text-muted-foreground max-w-paragraph mx-auto">
                How we work with every client, on every project.
              </p>
            </div>

            <div ref={elementRef} className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {principles.map((principle, index) => {
                const IconComponent = principle.icon;
                return (
                  <div 
                    key={index}
                    className="glass-panel p-8"
                    style={getRevealStyle(index)}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-display font-semibold text-foreground mb-3" style={{ fontSize: '18px', lineHeight: '1.3' }}>
                          {principle.title}
                        </h3>
                        <p className="text-muted-foreground leading-relaxed">
                          {principle.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

          </div>
        </div>
      </section>

      {/* What clients value */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="max-w-content mx-auto">
            
            <div className="text-center mb-16">
              <h2 className="font-display font-semibold text-foreground mb-6" style={{ fontSize: '32px', lineHeight: '1.2' }}>
                What clients value about working with us
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {clientValues.map((value, index) => (
                <div key={index} className="flex items-center gap-3 p-4">
                  <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0" />
                  <span className="text-muted-foreground">{value}</span>
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-20 bg-background-secondary">
        <div className="container mx-auto px-6">
          <div className="max-w-content mx-auto">
            
            <div className="text-center mb-16">
              <h2 className="font-display font-semibold text-foreground mb-6" style={{ fontSize: '32px', lineHeight: '1.2' }}>
                How we work
              </h2>
              <p className="text-muted-foreground max-w-paragraph mx-auto">
                Structured process that keeps projects on track and clients informed.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              
              <div className="glass-panel p-6 text-center">
                <div className="text-2xl font-bold text-primary mb-3">Weekly</div>
                <h3 className="font-medium text-foreground mb-2">Progress Updates</h3>
                <p className="text-sm text-muted-foreground">
                  Demo what we built, discuss next steps, address any questions.
                </p>
              </div>

              <div className="glass-panel p-6 text-center">
                <div className="text-2xl font-bold text-primary mb-3">Daily</div>
                <h3 className="font-medium text-foreground mb-2">Internal Standups</h3>
                <p className="text-sm text-muted-foreground">
                  Our team stays aligned on priorities, blockers, and timeline.
                </p>
              </div>

              <div className="glass-panel p-6 text-center">
                <div className="text-2xl font-bold text-primary mb-3">Real-time</div>
                <h3 className="font-medium text-foreground mb-2">Slack Communication</h3>
                <p className="text-sm text-muted-foreground">
                  Quick questions, approvals, and feedback without formal meetings.
                </p>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* Example outcomes */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="max-w-content mx-auto">
            
            <div className="text-center mb-16">
              <h2 className="font-display font-semibold text-foreground mb-6" style={{ fontSize: '32px', lineHeight: '1.2' }}>
                Example outcomes
              </h2>
              <p className="text-muted-foreground max-w-paragraph mx-auto">
                These are example scenarios, not specific client metrics.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {exampleOutcomes.map((example, index) => (
                <div key={index} className="glass-panel p-6 text-center">
                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 border border-primary/20 mb-4">
                    <span className="text-primary font-semibold text-xs">{example.label}</span>
                  </div>
                  <h3 className="font-medium text-foreground mb-3">{example.type}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {example.outcome}
                  </p>
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>

        {/* CTA Section */}
        <section className="py-20 bg-background-secondary">
          <div className="container mx-auto px-6">
            <div className="max-w-content mx-auto text-center">
              
              <h2 className="font-display font-semibold text-foreground mb-6" style={{ fontSize: '40px', lineHeight: '1.1' }}>
                Ready to work together?
              </h2>
              <p className="text-muted-foreground mb-8 max-w-paragraph mx-auto">
                Let's discuss your project and see if we're a good fit.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button variant="default" size="lg" className="group bg-primary hover:bg-primary/90 text-primary-foreground tap-target">
                    Start a project
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button variant="ghost" size="lg" className="text-muted-foreground hover:text-foreground tap-target">
                    View services
                  </Button>
                </Link>
              </div>

            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default About;